import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IconButton, Button, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ComputerCronJobModel from "../../models/computerCronJobModel";
import ScheduledTaskRow from "../../components/ScheduledTaskRow";
import ScheduledTaskForm from "../../components/ScheduledTaskForm";
import styles from "./ScheduledTasks.module.css";

const BRAND = "#a388f3";
const BRAND_HOVER = "#8b6fe0";

export default function ScheduledTasks() {
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const uid = urlParams.get("uid");
  const orgId = urlParams.get("organizationId");

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);

  const fetchJobs = useCallback(async () => {
    if (!uid) return;
    const result = await ComputerCronJobModel.list(uid);
    setJobs(result);
    setLoading(false);
  }, [uid]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleToggle = async (jobId) => {
    const result = await ComputerCronJobModel.toggle(jobId, uid);
    if (result) {
      setJobs((prev) =>
        prev.map((j) => (j.id === jobId ? { ...j, enabled: result.enabled } : j))
      );
    }
  };

  const handleDelete = async (jobId) => {
    const ok = await ComputerCronJobModel.delete(jobId, uid);
    if (ok) {
      setJobs((prev) => prev.filter((j) => j.id !== jobId));
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormOpen(true);
  };

  const handleCreate = () => {
    setEditingJob(null);
    setFormOpen(true);
  };

  const handleRunNow = async (jobId) => {
    const result = await ComputerCronJobModel.runNow(jobId, uid);
    if (result) {
      setJobs((prev) =>
        prev.map((j) =>
          j.id === jobId
            ? { ...j, state: { ...j.state, lastStatus: "queued" } }
            : j
        )
      );
    }
  };

  const handleSave = async (payload, existing) => {
    if (existing) {
      const updated = await ComputerCronJobModel.update(existing.id, uid, payload);
      if (!updated) throw new Error("Failed to update task");
      setJobs((prev) => prev.map((j) => (j.id === existing.id ? { ...j, ...updated } : j)));
    } else {
      const created = await ComputerCronJobModel.create({ ...payload, uid, orgId });
      if (!created) throw new Error("Failed to create task");
      setJobs((prev) => [created, ...prev]);
    }
    setFormOpen(false);
    setEditingJob(null);
  };

  const goBack = () => {
    const params = new URLSearchParams(window.location.search);
    navigate(`/?${params.toString()}`);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <IconButton size="small" onClick={goBack} sx={{ color: "var(--text-secondary)" }}>
          <ArrowBackIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <span className={styles.headerTitle}>Scheduled Tasks</span>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={{
            textTransform: "none",
            backgroundColor: BRAND,
            color: "#fff",
            fontSize: 12,
            "&:hover": { backgroundColor: BRAND_HOVER },
            borderRadius: 1.5,
            px: 1.5,
            py: 0.5,
          }}
        >
          New Task
        </Button>
      </div>

      <div className={styles.body}>
        {loading ? (
          <div className={styles.loadingWrap}>
            <CircularProgress size={24} sx={{ color: BRAND }} />
          </div>
        ) : jobs.length === 0 ? (
          <div className={styles.emptyState}>
            <ScheduleIcon className={styles.emptyIcon} />
            <div className={styles.emptyTitle}>No scheduled tasks yet</div>
            <div className={styles.emptySubtitle}>
              Create a scheduled task to have your AI agent run browser tasks automatically.
            </div>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{
                textTransform: "none",
                borderColor: BRAND,
                color: BRAND,
                fontSize: 12,
                mt: 1,
                "&:hover": { borderColor: BRAND_HOVER, backgroundColor: "var(--brand-soft)" },
              }}
            >
              Create your first task
            </Button>
          </div>
        ) : (
          jobs.map((job) => (
            <ScheduledTaskRow
              key={job.id}
              job={job}
              uid={uid}
              onToggle={handleToggle}
              onDelete={handleDelete}
              onEdit={handleEdit}
              onRunNow={handleRunNow}
            />
          ))
        )}
      </div>

      <ScheduledTaskForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingJob(null); }}
        onSave={handleSave}
        editingJob={editingJob}
      />
    </div>
  );
}
